import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { Deck } from '../types';

export default function DeckBuilderPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newDeck, setNewDeck] = useState({
    name: '',
    description: '',
    isPublic: false,
  });

  const queryClient = useQueryClient();

  const { data: decks, isLoading } = useQuery({
    queryKey: ['decks'],
    queryFn: () => apiClient.getDecks(),
  });

  const createDeckMutation = useMutation({
    mutationFn: (deck: Partial<Deck>) => apiClient.createDeck(deck),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['decks'] });
      setShowCreateForm(false);
      setNewDeck({ name: '', description: '', isPublic: false });
    },
  });

  const deleteDeckMutation = useMutation({
    mutationFn: (id: number) => apiClient.deleteDeck(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['decks'] });
    },
  });

  const handleCreateDeck = (e: React.FormEvent) => {
    e.preventDefault();
    createDeckMutation.mutate(newDeck);
  };

  const handleDeleteDeck = (id: number) => {
    if (window.confirm('Are you sure you want to delete this deck?')) {
      deleteDeckMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading decks...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deck Builder</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage your custom card decks
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn-primary"
        >
          Create New Deck
        </button>
      </div>

      {/* Create Deck Form */}
      {showCreateForm && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Deck</h3>
          <form onSubmit={handleCreateDeck} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Deck Name
              </label>
              <input
                type="text"
                id="name"
                value={newDeck.name}
                onChange={(e) => setNewDeck({ ...newDeck, name: e.target.value })}
                className="input-field"
                placeholder="Enter deck name"
                required
              />
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                value={newDeck.description}
                onChange={(e) => setNewDeck({ ...newDeck, description: e.target.value })}
                className="input-field"
                rows={3}
                placeholder="Enter deck description"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                checked={newDeck.isPublic}
                onChange={(e) => setNewDeck({ ...newDeck, isPublic: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                Make this deck public
              </label>
            </div>
            <div className="flex space-x-3">
              <button type="submit" className="btn-primary" disabled={createDeckMutation.isPending}>
                {createDeckMutation.isPending ? 'Creating...' : 'Create Deck'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Decks Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {decks?.map((deck) => (
          <div key={deck.id} className="card card-hover p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">{deck.name}</h3>
                <p className="mt-1 text-sm text-gray-500">{deck.description}</p>
                <div className="mt-2 flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    deck.isPublic 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {deck.isPublic ? 'Public' : 'Private'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {deck.cards?.length || 0} cards
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="text-primary-600 hover:text-primary-500 text-sm font-medium">
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteDeck(deck.id)}
                  className="text-red-600 hover:text-red-500 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              Created {new Date(deck.createdAt).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>

      {decks?.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No decks</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new deck.</p>
        </div>
      )}
    </div>
  );
}
